import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Ruler, Calendar, Plus, Check } from "lucide-react";

const MODES = [
  { key: "standard_size", label: "Standard size", sub: "Stock sizes only" },
  { key: "saved_profile", label: "Use saved measurement", sub: "Pick from your profiles" },
  { key: "new_measurement", label: "Add new measurement", sub: "Saves to your account" },
  { key: "appointment_required", label: "Request measurement appointment", sub: "Shop / Home / Video" },
];

/**
 * CustomFitPicker — shown above Add-to-Cart on stitching-enabled products.
 * Controls what `extras` payload the parent sends to `/cart/items`:
 *   { measurement_mode, clothing_type_id?, measurement_profile_id?, stitching_notes?, appointment_requested? }
 * Parent owns state via the `value` / `onChange` pair.
 */
export default function CustomFitPicker({ product, value, onChange }) {
  const { user } = useAuth();
  const supported = product?.supports_custom_measurement;
  const allowedSlugs = product?.supported_clothing_type_slugs || [];

  const [types, setTypes] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!supported) return;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const [t, p] = await Promise.all([
          api.get("/clothing-types"),
          user ? api.get("/measurements/profiles") : Promise.resolve({ data: [] }),
        ]);
        if (!alive) return;
        const filtered = (t.data || []).filter((x) =>
          allowedSlugs.length === 0 || allowedSlugs.includes(x.slug),
        );
        setTypes(filtered);
        setProfiles(p.data || []);
      } catch {
        if (alive) { setTypes([]); setProfiles([]); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported, user?.id, product?.id]);

  const applicableTypeIds = useMemo(() => new Set(types.map((t) => t.id)), [types]);

  const applicableProfiles = useMemo(
    () => profiles.filter((p) => applicableTypeIds.has(p.clothing_type_id)),
    [profiles, applicableTypeIds],
  );

  // Auto-preselect default profile when the user picks saved_profile mode
  useEffect(() => {
    if (value?.measurement_mode !== "saved_profile") return;
    if (value?.measurement_profile_id) return;
    const def = applicableProfiles.find((p) => p.is_default) || applicableProfiles[0];
    if (def) {
      onChange({
        ...value,
        measurement_profile_id: def.id,
        clothing_type_id: def.clothing_type_id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.measurement_mode, applicableProfiles.length]);

  if (!supported) return null;

  const mode = value?.measurement_mode || "standard_size";
  const setMode = (k) => {
    const next = { measurement_mode: k };
    if (k === "standard_size") Object.assign(next, { appointment_requested: false });
    if (k === "appointment_required") next.appointment_requested = true;
    onChange(next);
  };

  return (
    <section className="border border-[hsl(var(--kb-border))] bg-white p-4 md:p-5" data-testid="custom-fit-picker">
      <div className="flex items-center gap-2 mb-3">
        <Ruler size={14} />
        <div className="overline">Custom Fit</div>
        {product?.stitching_charge ? (
          <span className="ml-auto text-xs text-[hsl(var(--kb-muted))]">
            + ₹{product.stitching_charge} stitching
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        {MODES.map((m) => {
          const active = mode === m.key;
          const disabled = m.key === "saved_profile" && !user;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => !disabled && setMode(m.key)}
              disabled={disabled}
              className={`text-left p-3 border text-xs transition-all disabled:opacity-50 ${
                active
                  ? "border-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-surface-alt))]"
                  : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]"
              }`}
              data-testid={`fit-mode-${m.key}`}
            >
              <div className="flex items-center gap-1 font-medium">
                {active && <Check size={12} className="text-[hsl(var(--kb-primary))]" />}
                {m.label}
              </div>
              <div className="text-[10px] text-[hsl(var(--kb-muted))] mt-0.5">{m.sub}</div>
            </button>
          );
        })}
      </div>

      {/* Saved profile picker */}
      {mode === "saved_profile" && (
        <div data-testid="fit-saved-block">
          {loading ? (
            <div className="h-9 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
          ) : applicableProfiles.length === 0 ? (
            <div className="text-xs text-[hsl(var(--kb-muted))]">
              No saved measurements yet for this type.{" "}
              <Link to="/account?tab=measurements" className="underline">Add one →</Link>
            </div>
          ) : (
            <select
              className="kb-input"
              value={value?.measurement_profile_id || ""}
              onChange={(e) => {
                const p = applicableProfiles.find((x) => x.id === e.target.value);
                onChange({
                  ...value,
                  measurement_profile_id: p?.id,
                  clothing_type_id: p?.clothing_type_id,
                });
              }}
              data-testid="fit-profile-select"
            >
              {applicableProfiles.map((p) => {
                const t = types.find((x) => x.id === p.clothing_type_id);
                return (
                  <option key={p.id} value={p.id}>
                    {p.profile_name}{p.is_default ? " · default" : ""} · {t?.name}
                  </option>
                );
              })}
            </select>
          )}
        </div>
      )}

      {/* Appointment & new-measurement nudges */}
      {mode === "new_measurement" && (
        <div className="text-xs text-[hsl(var(--kb-muted))]" data-testid="fit-new-block">
          <Link to="/account?tab=measurements" className="inline-flex items-center gap-1 underline hover:no-underline">
            <Plus size={12} /> Open the measurement form in Account — it only takes a minute.
          </Link>
        </div>
      )}
      {mode === "appointment_required" && (
        <div className="text-xs text-[hsl(var(--kb-muted))]" data-testid="fit-appointment-block">
          <Link to="/account?tab=appointments" className="inline-flex items-center gap-1 underline hover:no-underline">
            <Calendar size={12} /> Book your measurement slot from Account (shop / home / video).
          </Link>
        </div>
      )}

      {/* Stitching notes for non-standard modes */}
      {mode !== "standard_size" && (
        <textarea
          rows={2}
          className="kb-input mt-3 text-sm"
          maxLength={500}
          placeholder="Stitching notes (e.g. sleeve length preference, padding, etc.)"
          value={value?.stitching_notes || ""}
          onChange={(e) => onChange({ ...value, stitching_notes: e.target.value })}
          data-testid="fit-stitching-notes"
        />
      )}
    </section>
  );
}
